/**
 * useSleepMode — the player's sleep-mode state machine.
 *
 * Two triggers, command wins over schedule:
 * - SLEEP/WAKE MQTT commands (ad-hoc, operator-driven) — sticky until the
 *   opposite command arrives or the page reloads.
 * - The manifest's scheduled sleep window (offline-capable) — re-evaluated
 *   every 60s on the same cadence as the schedule evaluator.
 *
 * While active, PlayerView suspends playback (usePlayback `suspended`), dims
 * the stage with a CSS brightness filter, publishes window.__doohSleep for
 * the device backlight daemon, and renders SleepOverlay.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ScheduleManifest } from '@dooh/shared';
import { evaluateSleepWindow } from './sleep-window';
import type { SleepOverlayVariant } from './SleepOverlay';

/** Ad-hoc override from a SLEEP/WAKE MQTT command. */
export interface SleepCommandState {
  mode: 'sleep' | 'wake';
  variant?: SleepOverlayVariant;
}

export interface SleepModeState {
  active: boolean;
  variant: SleepOverlayVariant;
  /** Target backlight brightness 0–1 while asleep (default 0.15). */
  brightness: number;
  /** Wake time label ("HH:MM") when the window's end is known. */
  wakeAt?: string;
  /** 0–1 progress through the scheduled window (scheduled sleep only). */
  progress?: number;
  source: 'schedule' | 'command' | null;
}

export const DEFAULT_SLEEP_BRIGHTNESS = 0.15;
export const DEFAULT_SLEEP_VARIANT: SleepOverlayVariant = 'night-drift';

/** Re-evaluation cadence — matches usePlayback's schedule tick. */
export const SLEEP_EVALUATION_INTERVAL_MS = 60_000;

const AWAKE: SleepModeState = {
  active: false,
  variant: DEFAULT_SLEEP_VARIANT,
  brightness: DEFAULT_SLEEP_BRIGHTNESS,
  source: null,
};

/** Pure state computation, exported for tests. */
export function computeSleepState(
  manifest: ScheduleManifest | null,
  command: SleepCommandState | null,
  now: Date,
): SleepModeState {
  if (command?.mode === 'sleep') {
    return {
      active: true,
      variant: command.variant ?? DEFAULT_SLEEP_VARIANT,
      brightness: DEFAULT_SLEEP_BRIGHTNESS,
      source: 'command',
    };
  }
  if (command?.mode === 'wake') {
    return AWAKE;
  }

  // A sleep campaign can define several windows (e.g. overnight + a midday
  // siesta), so evaluate them all. `sleep` is the pre-campaign single-window
  // field, still read when an older CMS omits `sleepWindows`.
  const windows = manifest?.sleepWindows ?? (manifest?.sleep ? [manifest.sleep] : []);
  if (windows.length === 0) return AWAKE;

  for (const window of windows) {
    const evaluation = evaluateSleepWindow(window, now, manifest?.timezone);
    if (!evaluation.active) continue;

    return {
      active: true,
      variant: window.variant ?? DEFAULT_SLEEP_VARIANT,
      brightness: window.brightness ?? DEFAULT_SLEEP_BRIGHTNESS,
      wakeAt: evaluation.wakeAt,
      progress: evaluation.progress,
      source: 'schedule',
    };
  }

  return AWAKE;
}

export function useSleepMode(args: {
  manifest: ScheduleManifest | null;
  command: SleepCommandState | null;
}): SleepModeState {
  const { manifest, command } = args;
  // Tick forces a recompute as wall-clock time crosses window boundaries.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), SLEEP_EVALUATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return useMemo(
    () => computeSleepState(manifest, command, new Date()),
    // tick is the recompute trigger; manifest/command are the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manifest, command, tick],
  );
}
